// @ts-nocheck
import React, { createContext, useContext, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

export type TaskType = 'dialogue' | 'fx' | 'music' | 'text-to-video' | 'image-to-video';
export type TaskStatus = 'pending' | 'generating' | 'completed' | 'error';

export interface GenerationTask {
  id: string;
  type: TaskType;
  status: TaskStatus;
  progress?: number; // 0-100
  error?: string;
  resultUrl?: string;
  createdAt: number;
}

interface GenerationContextType {
  tasks: GenerationTask[];
  addTask: (type: TaskType) => string;
  updateTask: (id: string, updates: Partial<GenerationTask>) => void;
  removeTask: (id: string) => void;
}

const GenerationContext = createContext<GenerationContextType | undefined>(undefined);

export function GenerationProvider({ children }: { children: React.ReactNode }) {
  const [tasks, setTasks] = useState<GenerationTask[]>([]);

  const addTask = useCallback((type: TaskType) => {
    const id = uuidv4();
    setTasks(prev => [...prev, {
      id,
      type,
      status: 'generating',
      createdAt: Date.now()
    }]);
    return id;
  }, []);

  const updateTask = useCallback((id: string, updates: Partial<GenerationTask>) => {
    setTasks(prev => prev.map(task =>
      task.id === id ? { ...task, ...updates } : task
    ));
  }, []);

  const removeTask = useCallback((id: string) => {
    setTasks(prev => prev.filter(task => task.id !== id));
  }, []);

  return (
    <GenerationContext.Provider value={{ tasks, addTask, updateTask, removeTask }}>
      {children}
    </GenerationContext.Provider>
  );
}

// No-op fallback so consumers don't crash when not wrapped in a provider.
const NOOP_GENERATION: GenerationContextType = {
  tasks: [],
  addTask: () => '',
  updateTask: () => {},
  removeTask: () => {},
};

export function useGeneration() {
  const context = useContext(GenerationContext);
  return context ?? NOOP_GENERATION;
}
